import { useState, useRef, useEffect, useCallback } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import DOMPurify from 'dompurify'
import MarkdownRenderer from './MarkdownRenderer'
import { useSSEConnected } from '../hooks/useSSE'

// ═══ Types ═══════════════════════════════════════════════════════

type ContentType = 'text' | 'image' | 'html' | 'video' | 'audio'

interface MagicBoxItem {
  id: number
  content_type: ContentType
  title?: string
  content: string
  posted_by?: string
  created_at: string
  folder_id?: number | null
}

interface MagicBoxFolder {
  id: number
  conversation_id: string
  name: string
  created_by?: string
  created_at: string
  vault_path?: string | null
}

interface Props {
  conversationId: string
  onClose?: () => void
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ═══ Content-Type Registry ═══════════════════════════════════════

function ImageRenderer({ item }: { item: MagicBoxItem }) {
  return (
    <img
      src={item.content}
      alt={item.title || ''}
      className="rounded-lg w-full"
      style={{ objectFit: 'contain' }}
    />
  )
}

function VideoRenderer({ item }: { item: MagicBoxItem }) {
  return (
    <video
      src={item.content}
      controls
      className="rounded-lg w-full"
      style={{ maxHeight: '100%' }}
    />
  )
}

function AudioRenderer({ item }: { item: MagicBoxItem }) {
  return (
    <div className="flex flex-col items-center gap-3 py-6">
      {item.title && (
        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
          {item.title}
        </span>
      )}
      <audio src={item.content} controls className="w-full" />
    </div>
  )
}

function HtmlRenderer({ item }: { item: MagicBoxItem }) {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const origin = window.location.origin
  const contentWithAbsoluteUrls = item.content
    .replace(/src="\/([^"]*?)"/g, `src="${origin}/$1"`)
    .replace(/src='\/([^']*?)'/g, `src='${origin}/$1'`)
    .replace(/url\(\/([^)]*?)\)/g, `url(${origin}/$1)`)

  const styleTags: string[] = []
  const contentWithoutStyles = contentWithAbsoluteUrls.replace(
    /<style[^>]*>[\s\S]*?<\/style>/gi,
    (match) => { styleTags.push(match); return '' }
  )
  const sanitized = styleTags.join('\n') + DOMPurify.sanitize(contentWithoutStyles, {
    ADD_ATTR: ['style'],
  })

  const srcDoc = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { background: #0d1117; color: #e6edf3; font-family: 'Inter', -apple-system, sans-serif; font-size: 14px; line-height: 1.6; padding: 12px; overflow-x: hidden; }
  img { max-width: 100%; height: auto; border-radius: 8px; display: block; }
  a { color: #9C59F5; }
</style></head><body>${sanitized}</body></html>`

  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcDoc}
      sandbox="allow-same-origin"
      title={item.title || 'Magic Box content'}
      className="w-full rounded-lg border-0"
      style={{ minHeight: 300, height: '100%', background: '#0d1117' }}
      onLoad={() => {
        try {
          const body = iframeRef.current?.contentDocument?.body
          if (body) {
            iframeRef.current!.style.height = `${body.scrollHeight + 24}px`
          }
        } catch { /* cross-origin fallback */ }
      }}
    />
  )
}

function TextRenderer({ item }: { item: MagicBoxItem }) {
  return (
    <div className="text-sm leading-relaxed" style={{ color: 'var(--text-primary)' }}>
      <MarkdownRenderer content={item.content} />
    </div>
  )
}

function MagicBoxContent({ item }: { item: MagicBoxItem }) {
  switch (item.content_type) {
    case 'image': return <ImageRenderer item={item} />
    case 'video': return <VideoRenderer item={item} />
    case 'audio': return <AudioRenderer item={item} />
    case 'html':  return <HtmlRenderer item={item} />
    default:      return <TextRenderer item={item} />
  }
}

// ═══ Content Type Icon ═══════════════════════════════════════════

function contentIcon(type: ContentType): string {
  switch (type) {
    case 'image': return '🖼'
    case 'video': return '🎬'
    case 'audio': return '🔊'
    case 'html': return '📄'
    default: return '📝'
  }
}

// ═══ Folder View ════════════════════════════════════════════════

function VaultFolderRow({ folder, conversationId, onSelectItem }: {
  folder: MagicBoxFolder
  conversationId: string
  onSelectItem: (item: MagicBoxItem) => void
}) {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<MagicBoxItem[]>([])
  const [loaded, setLoaded] = useState(false)

  const loadItems = async () => {
    if (loaded) { setOpen(!open); return }
    try {
      const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
      const res = await fetch(`/api/conversations/${conversationId}/magic-box/vault-folder/${folder.id}`, { headers })
      if (res.ok) {
        const data = await res.json()
        setItems(data.items || [])
      }
    } catch {}
    setLoaded(true)
    setOpen(true)
  }

  return (
    <div>
      <div
        onClick={loadItems}
        className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors hover:bg-[var(--bg-elevated)]"
      >
        <span className="text-xs" style={{ opacity: 0.7 }}>{open ? '▼' : '▶'}</span>
        <span style={{ fontSize: 13 }}>📂</span>
        <span className="text-xs font-medium flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{folder.name}</span>
        {loaded && (
          <span className="text-xs px-1.5 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontSize: 10 }}>
            {items.length}
          </span>
        )}
      </div>
      {open && items.length > 0 && (
        <div className="ml-5 flex flex-col gap-0.5">
          {items.map(item => (
            <div
              key={item.id}
              onClick={() => onSelectItem(item)}
              className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer transition-colors hover:bg-[var(--bg-elevated)]"
            >
              <span style={{ fontSize: 11 }}>📄</span>
              <span className="text-xs truncate flex-1" style={{ color: 'var(--text-primary)' }}>
                {item.title || 'Untitled'}
              </span>
            </div>
          ))}
        </div>
      )}
      {open && items.length === 0 && loaded && (
        <div className="ml-5 px-2 py-1 text-xs" style={{ color: 'var(--text-muted)', opacity: 0.5 }}>
          Empty
        </div>
      )}
    </div>
  )
}

function FolderView({
  items, folders, onSelectItem, onCreateFolder, onMoveItem, conversationId,
}: {
  items: MagicBoxItem[]
  folders: MagicBoxFolder[]
  onSelectItem: (item: MagicBoxItem) => void
  onCreateFolder: (name: string) => void
  onMoveItem: (itemId: number, folderId: number | null) => void
  conversationId: string
}) {
  const [newFolderName, setNewFolderName] = useState('')
  const [showNewFolder, setShowNewFolder] = useState(false)

  const vaultFolders = folders.filter(f => f.vault_path)
  const dbFolders = folders.filter(f => !f.vault_path)

  const inbox = items.filter(i => !i.folder_id)
  const folderMap = new Map<number, MagicBoxItem[]>()
  for (const item of items) {
    if (item.folder_id) {
      const arr = folderMap.get(item.folder_id) || []
      arr.push(item)
      folderMap.set(item.folder_id, arr)
    }
  }

  return (
    <div className="flex flex-col gap-1 p-2">
      {/* Inbox */}
      <FolderRow
        icon="📥"
        name="Inbox"
        count={inbox.length}
        items={inbox}
        onSelectItem={onSelectItem}
        defaultOpen={true}
        folderId={null}
        onDropItem={onMoveItem}
      />

      {/* Vault-backed folders */}
      {vaultFolders.map(f => (
        <VaultFolderRow key={f.id} folder={f} conversationId={conversationId} onSelectItem={onSelectItem} />
      ))}

      {/* Database-backed folders */}
      {dbFolders.map(f => (
        <FolderRow
          key={f.id}
          icon="📁"
          name={f.name}
          count={folderMap.get(f.id)?.length || 0}
          items={folderMap.get(f.id) || []}
          onSelectItem={onSelectItem}
          folderId={f.id}
          onDropItem={onMoveItem}
        />
      ))}

      {/* New folder */}
      {showNewFolder ? (
        <div className="flex gap-1 mt-1 px-1">
          <input
            autoFocus
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && newFolderName.trim()) {
                onCreateFolder(newFolderName.trim())
                setNewFolderName('')
                setShowNewFolder(false)
              }
              if (e.key === 'Escape') setShowNewFolder(false)
            }}
            placeholder="Folder name..."
            className="flex-1 text-xs rounded px-2 py-1"
            style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)', outline: 'none' }}
          />
        </div>
      ) : (
        <button
          onClick={() => setShowNewFolder(true)}
          className="text-xs text-left px-2 py-1 mt-1 rounded transition-colors"
          style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          + New folder
        </button>
      )}
    </div>
  )
}

function formatItemTime(ts: string): string {
  if (!ts) return ''
  const d = new Date(ts)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  if (diffMs < 60000) return 'just now'
  if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`
  if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

type SortMode = 'newest' | 'oldest' | 'name'

function FolderRow({
  icon, name, count, items, onSelectItem, defaultOpen, folderId, onDropItem,
}: {
  icon: string
  name: string
  count: number
  items: MagicBoxItem[]
  onSelectItem: (item: MagicBoxItem) => void
  defaultOpen?: boolean
  folderId?: number | null
  onDropItem?: (itemId: number, folderId: number | null) => void
}) {
  const [open, setOpen] = useState(defaultOpen || false)
  const [sort, setSort] = useState<SortMode>('newest')
  const [dragOver, setDragOver] = useState(false)

  const sorted = [...items].sort((a, b) => {
    if (sort === 'name') return (a.title || '').localeCompare(b.title || '')
    if (sort === 'oldest') return (a.created_at || '').localeCompare(b.created_at || '')
    return (b.created_at || '').localeCompare(a.created_at || '')
  })

  return (
    <div
      onDragOver={onDropItem ? e => { e.preventDefault(); setDragOver(true) } : undefined}
      onDragLeave={onDropItem ? () => setDragOver(false) : undefined}
      onDrop={onDropItem ? e => {
        e.preventDefault()
        setDragOver(false)
        const itemId = e.dataTransfer.getData('text/plain')
        if (itemId) onDropItem(parseInt(itemId), folderId ?? null)
      } : undefined}
    >
      <div
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors hover:bg-[var(--bg-elevated)]"
        style={{ background: dragOver ? 'rgba(133,111,200,0.15)' : undefined, border: dragOver ? '1px dashed var(--accent)' : '1px solid transparent' }}
      >
        <span className="text-xs" style={{ opacity: 0.7 }}>{open ? '▼' : '▶'}</span>
        <span style={{ fontSize: 13 }}>{icon}</span>
        <span className="text-xs font-medium flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{name}</span>
        {count > 0 && (
          <span className="text-xs px-1.5 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontSize: 10 }}>
            {count}
          </span>
        )}
      </div>
      {open && items.length > 0 && (
        <>
          <div className="ml-5 flex gap-1 px-2 mb-0.5">
            {(['newest', 'oldest', 'name'] as SortMode[]).map(s => (
              <button
                key={s}
                onClick={() => setSort(s)}
                className="text-xs"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '1px 4px',
                  color: sort === s ? 'var(--accent)' : 'var(--text-muted)', fontSize: 9,
                }}
              >
                {s === 'newest' ? '↓New' : s === 'oldest' ? '↑Old' : 'A-Z'}
              </button>
            ))}
          </div>
          <div className="ml-5 flex flex-col gap-0.5">
            {sorted.map(item => (
              <div
                key={item.id}
                draggable={typeof item.id === 'number'}
                onDragStart={e => { e.dataTransfer.setData('text/plain', String(item.id)); e.dataTransfer.effectAllowed = 'move' }}
                onClick={() => onSelectItem(item)}
                className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer transition-colors hover:bg-[var(--bg-elevated)]"
                style={{ cursor: 'grab' }}
              >
                <span style={{ fontSize: 11 }}>{contentIcon(item.content_type)}</span>
                <div className="flex-1 min-w-0">
                  <span className="text-xs truncate block" style={{ color: 'var(--text-primary)' }}>
                    {item.title || item.content.substring(0, 40)}
                  </span>
                  <span className="text-xs block" style={{ color: 'var(--text-muted)', fontSize: 9 }}>
                    {item.posted_by && `${item.posted_by} · `}{formatItemTime(item.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {open && items.length === 0 && (
        <div className="ml-5 px-2 py-1 text-xs" style={{ color: 'var(--text-muted)', opacity: 0.5 }}>
          Empty
        </div>
      )}
    </div>
  )
}

// ═══ Item Nav Bar ═══════════════════════════════════════════════

function ItemNavBar({
  item, items, folders, onBack, onNavigate, onMoveToFolder,
}: {
  item: MagicBoxItem
  items: MagicBoxItem[]
  folders: MagicBoxFolder[]
  onBack: () => void
  onNavigate: (item: MagicBoxItem) => void
  onMoveToFolder: (itemId: number, folderId: number | null) => void
  conversationId: string
}) {
  const [showFileMenu, setShowFileMenu] = useState(false)

  const sameFolder = items.filter(i => i.folder_id === item.folder_id)
  const currentIdx = sameFolder.findIndex(i => i.id === item.id)
  const prev = currentIdx > 0 ? sameFolder[currentIdx - 1] : null
  const next = currentIdx < sameFolder.length - 1 ? sameFolder[currentIdx + 1] : null

  return (
    <div className="flex items-center gap-1 px-2 py-1.5 shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
      <button
        onClick={onBack}
        className="text-xs px-1.5 py-0.5 rounded"
        style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
        title="Back to folders"
      >
        ←
      </button>
      <span className="text-xs font-medium truncate flex-1" style={{ color: 'var(--text-primary)' }}>
        {item.title || 'Untitled'}
      </span>
      <div className="relative">
        <button
          onClick={() => setShowFileMenu(!showFileMenu)}
          className="text-xs px-1.5 py-0.5 rounded"
          style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
          title="Move to folder"
        >
          File
        </button>
        {showFileMenu && (
          <div
            className="absolute right-0 top-6 z-50 rounded shadow-lg py-1 min-w-[140px]"
            style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}
          >
            <div
              onClick={() => { onMoveToFolder(item.id, null); setShowFileMenu(false) }}
              className="px-3 py-1.5 text-xs cursor-pointer hover:bg-[var(--bg-body)]"
              style={{ color: item.folder_id === null ? 'var(--accent)' : 'var(--text-primary)' }}
            >
              📥 Inbox
            </div>
            {folders.map(f => (
              <div
                key={f.id}
                onClick={() => { onMoveToFolder(item.id, f.id); setShowFileMenu(false) }}
                className="px-3 py-1.5 text-xs cursor-pointer hover:bg-[var(--bg-body)]"
                style={{ color: item.folder_id === f.id ? 'var(--accent)' : 'var(--text-primary)' }}
              >
                📁 {f.name}
              </div>
            ))}
          </div>
        )}
      </div>
      <button
        onClick={() => prev && onNavigate(prev)}
        disabled={!prev}
        className="text-xs px-1 py-0.5 rounded"
        style={{ color: prev ? 'var(--text-muted)' : 'var(--border)', background: 'none', border: 'none', cursor: prev ? 'pointer' : 'default' }}
      >
        ◀
      </button>
      <button
        onClick={() => next && onNavigate(next)}
        disabled={!next}
        className="text-xs px-1 py-0.5 rounded"
        style={{ color: next ? 'var(--text-muted)' : 'var(--border)', background: 'none', border: 'none', cursor: next ? 'pointer' : 'default' }}
      >
        ▶
      </button>
    </div>
  )
}

// ═══ MagicBox Component ═════════════════════════════════════════

export default function MagicBox({ conversationId }: Props) {
  const sseOk = useSSEConnected()
  const headers = getAuthHeaders()
  const queryClient = useQueryClient()
  const [selectedItem, setSelectedItem] = useState<MagicBoxItem | null>(null)
  const [minimized, _setMinimized] = useState(() => {
    const saved = localStorage.getItem(`mb-min-${conversationId}`)
    return saved === null ? true : saved === '1'
  })
  const setMinimized = (val: boolean) => {
    _setMinimized(val)
    localStorage.setItem(`mb-min-${conversationId}`, val ? '1' : '0')
  }
  useEffect(() => {
    const saved = localStorage.getItem(`mb-min-${conversationId}`)
    _setMinimized(saved === null ? true : saved === '1')
    setSelectedItem(null)
  }, [conversationId])
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data } = useQuery({
    queryKey: ['magicBox', conversationId],
    queryFn: async () => {
      const res = await fetch(`/api/conversations/${conversationId}/magic-box`, { headers })
      if (!res.ok) return null
      return res.json() as Promise<{ items: MagicBoxItem[]; current: MagicBoxItem | null }>
    },
    enabled: !!conversationId,
    refetchInterval: sseOk ? false : 30_000,
  })

  const { data: foldersData } = useQuery({
    queryKey: ['magicBoxFolders', conversationId],
    queryFn: async () => {
      const res = await fetch(`/api/conversations/${conversationId}/magic-box/folders`, { headers })
      if (!res.ok) return { folders: [] }
      return res.json() as Promise<{ folders: MagicBoxFolder[] }>
    },
    enabled: !!conversationId,
  })

  const createFolder = useMutation({
    mutationFn: async (name: string) => {
      await fetch(`/api/conversations/${conversationId}/magic-box/folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ name }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['magicBoxFolders', conversationId] })
    },
  })

  const moveItem = useMutation({
    mutationFn: async ({ itemId, folderId }: { itemId: number; folderId: number | null }) => {
      await fetch(`/api/conversations/${conversationId}/magic-box/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ folder_id: folderId }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['magicBox', conversationId] })
    },
  })

  const [panelWidth, setPanelWidth] = useState(() =>
    parseInt(localStorage.getItem('magicbox-width') || '380')
  )
  const panelRef = useRef<HTMLDivElement>(null)

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files)
    if (fileArray.length === 0) return
    setUploading(true)

    const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
    for (const file of fileArray) {
      try {
        const formData = new FormData()
        formData.append('file', file)
        const uploadRes = await fetch('/api/upload', {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        })
        if (!uploadRes.ok) throw new Error('Upload failed')
        const uploadData = await uploadRes.json()
        const fileUrl = uploadData.url || `/uploads/${uploadData.filename}`

        let contentType: ContentType = 'text'
        if (file.type.startsWith('image/')) contentType = 'image'
        else if (file.type.startsWith('video/')) contentType = 'video'
        else if (file.type.startsWith('audio/')) contentType = 'audio'

        await fetch(`/api/conversations/${conversationId}/magic-box`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({
            content: fileUrl,
            content_type: contentType,
            title: file.name,
            posted_by: 'joel',
          }),
        })
      } catch (err) {
        console.error('Magic Box upload failed:', err)
      }
    }

    setUploading(false)
    setDragging(false)
    queryClient.invalidateQueries({ queryKey: ['magicBox', conversationId] })
  }, [conversationId, queryClient])

  const items = data?.items || []
  const folders = foldersData?.folders || []

  if (minimized) {
    return (
      <div
        onClick={() => setMinimized(false)}
        className="shrink-0 flex items-center justify-center cursor-pointer transition-colors hover:bg-[var(--bg-elevated)]"
        style={{
          width: 24,
          borderRight: '1px solid var(--border)',
          background: 'var(--bg-body)',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
        }}
        title="Expand Magic Box"
      >
        <span className="text-xs font-medium" style={{ color: 'var(--text-muted)', letterSpacing: 2 }}>
          ✦
        </span>
      </div>
    )
  }

  return (
    <>
      <div
        ref={panelRef}
        className="flex flex-col overflow-hidden h-full"
        style={{
          width: panelWidth,
          minWidth: 120,
          flexShrink: 0,
          borderRight: '1px solid var(--border)',
          background: 'var(--bg-body)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-2 py-1 shrink-0" style={{ borderBottom: selectedItem ? 'none' : '1px solid var(--border)' }}>
          <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Magic Box</span>
          <button
            onClick={() => setMinimized(true)}
            className="text-xs px-1.5 py-0.5 rounded"
            style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
            title="Minimize"
          >
            ◀
          </button>
        </div>

        {/* Navigation bar when viewing an item */}
        {selectedItem && (
          <ItemNavBar
            item={selectedItem}
            items={items}
            folders={folders}
            onBack={() => setSelectedItem(null)}
            onNavigate={setSelectedItem}
            onMoveToFolder={(itemId, folderId) => moveItem.mutate({ itemId, folderId })}
            conversationId={conversationId}
          />
        )}

        {/* Content area */}
        <div className="flex-1 overflow-y-auto">
          {selectedItem ? (
            <div className="p-3">
              <MagicBoxContent item={selectedItem} />
            </div>
          ) : items.length > 0 ? (
            <FolderView
              items={items}
              folders={folders}
              onSelectItem={setSelectedItem}
              onCreateFolder={(name) => createFolder.mutate(name)}
              onMoveItem={(itemId, folderId) => moveItem.mutate({ itemId, folderId })}
              conversationId={conversationId}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center px-4" style={{ color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>✦</div>
              <div className="text-xs font-medium mb-1">Magic Box</div>
              <div className="text-xs" style={{ opacity: 0.6 }}>
                Content shared in this conversation will appear here — images, documents, diagrams, and more.
              </div>
            </div>
          )}
        </div>

        {/* File upload drop zone */}
        <div
          className="shrink-0 px-3 py-2"
          style={{ borderTop: '1px solid var(--border)' }}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files) }}
        >
          <div
            onClick={() => fileInputRef.current?.click()}
            className="rounded-lg text-center cursor-pointer transition-colors"
            style={{
              padding: '10px 8px',
              border: dragging ? '2px dashed var(--accent)' : '2px dashed var(--border)',
              background: dragging ? 'var(--accent-muted, rgba(27,73,101,0.1))' : 'transparent',
              color: 'var(--text-muted)',
              fontSize: 11,
            }}
          >
            {uploading ? 'Uploading…' : dragging ? 'Drop files here' : '+ Drop or click to upload'}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,audio/*,.pdf,.html"
            style={{ display: 'none' }}
            onChange={e => { if (e.target.files) handleFiles(e.target.files); e.target.value = '' }}
          />
        </div>
      </div>

      {/* Resize handle */}
      <div
        style={{ width: 5, cursor: 'col-resize', flexShrink: 0, background: 'transparent' }}
        onMouseEnter={e => { (e.target as HTMLElement).style.background = 'var(--border)' }}
        onMouseLeave={e => { (e.target as HTMLElement).style.background = 'transparent' }}
        onMouseDown={(e) => {
          e.preventDefault()
          const startX = e.clientX
          const startW = panelWidth
          const onMove = (ev: MouseEvent) => {
            const newW = Math.max(250, startW + (ev.clientX - startX))
            if (panelRef.current) panelRef.current.style.width = `${newW}px`
          }
          const onUp = (ev: MouseEvent) => {
            document.removeEventListener('mousemove', onMove)
            document.removeEventListener('mouseup', onUp)
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
            const finalW = Math.max(250, startW + (ev.clientX - startX))
            setPanelWidth(finalW)
            localStorage.setItem('magicbox-width', String(finalW))
          }
          document.body.style.cursor = 'col-resize'
          document.body.style.userSelect = 'none'
          document.addEventListener('mousemove', onMove)
          document.addEventListener('mouseup', onUp)
        }}
      />
    </>
  )
}
